
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight } from "lucide-react";

interface SummaryCardsProps {
  totalRevenue: number;
  totalUnpaidRevenue: number;
  totalPaidBills: number;
  totalPendingBills: number;
  totalCancelledBills: number;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0, // Remove decimal places
  }).format(value);
};

export const SummaryCards = ({
  totalRevenue,
  totalUnpaidRevenue,
  totalPaidBills,
  totalPendingBills,
  totalCancelledBills,
}: SummaryCardsProps) => {
  const navigate = useNavigate();

  const handleViewPendingBills = () => {
    navigate('/admin/subscriptions?tab=bills&status=pending');
  };

  const handleViewPaidBills = () => {
    navigate('/admin/subscriptions?tab=bills&status=paid');
  };

  const handleViewCancelledBills = () => {
    navigate('/admin/subscriptions?tab=bills&status=cancelled');
  };

  const handleViewRevenue = () => {
    navigate('/transaction-history');
  };

  // Log values for debugging purposes when props change
  useEffect(() => {
    console.log('Dashboard Revenue Values:', {
      totalRevenue: formatCurrency(totalRevenue),
      totalUnpaidRevenue: formatCurrency(totalUnpaidRevenue),
      totalPaidBills,
      totalPendingBills
    });
  }, [totalRevenue, totalUnpaidRevenue, totalPaidBills, totalPendingBills]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <Card className="p-6 border-l-4 border-l-green-500">
        <div className="flex flex-col space-y-2">
          <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Unpaid: {formatCurrency(totalUnpaidRevenue)}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              From {totalPaidBills} paid transactions
            </p>
            <Button 
              variant="link" 
              className="mt-1 p-0 h-auto text-xs text-blue-600 hover:text-blue-800 flex items-center"
              onClick={handleViewRevenue}
            >
              View Transaction History
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      </Card>
      
      <Card className="p-6">
        <div className="flex flex-col h-full">
          <h3 className="text-sm font-medium text-gray-500">Paid Bills</h3>
          <p className="text-2xl font-bold mt-2">{totalPaidBills}</p>
          <Button 
            variant="link" 
            className="mt-2 p-0 h-auto text-sm text-blue-600 hover:text-blue-800 justify-start"
            onClick={handleViewPaidBills}
          >
            See Detail
          </Button>
        </div>
      </Card>
      
      <Card className="p-6">
        <div className="flex flex-col h-full">
          <h3 className="text-sm font-medium text-gray-500">Pending Bills</h3>
          <p className="text-2xl font-bold mt-2">{totalPendingBills}</p>
          <Button 
            variant="link" 
            className="mt-2 p-0 h-auto text-sm text-blue-600 hover:text-blue-800 justify-start"
            onClick={handleViewPendingBills}
          >
            See Detail
          </Button>
        </div>
      </Card>
      
      <Card className="p-6">
        <div className="flex flex-col h-full">
          <h3 className="text-sm font-medium text-gray-500">Cancelled Bills</h3>
          <p className="text-2xl font-bold mt-2">{totalCancelledBills}</p>
          <Button 
            variant="link" 
            className="mt-2 p-0 h-auto text-sm text-blue-600 hover:text-blue-800 justify-start"
            onClick={handleViewCancelledBills}
          >
            See Detail
          </Button>
        </div>
      </Card>
    </div>
  );
};
